import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import i18n from 'i18n/i18n';
import _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import Header from '../../components/Header';
import Description from '../../components/Description';
import FileUploader from '../../components/FileUploader/FileUploader';
import { Button } from '../../components/Features/components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import { DeleteIcon } from 'theme/icons';
import { showModal } from 'redux/modules/modal';
import { addCompanyFont, removeCompanyFont, showErrorPopup } from 'redux/modules/companies';
import { PROGRESS, changeCursor } from '../../utils/cursor';
import { FONTS_ACCEPTED_FILE_TYPES } from '../../config';
import CenteredContent from 'components/CenteredContent/CenteredContent';
import StickyHeader from '../../components/StickyHeader/StickyHeader';
import AppError from '../../errors/apperror';
import ERRORS from '../../errors/errorids';

const localize = i18n.loc.localize;

const styles = {
	uploadButton: {
		marginBottom: 20
	}
};

@withStyles(styles)
class Fonts extends Component {

	constructor() {
		super();
		this.uploader = null;
		this.handleUpload = this.handleUpload.bind(this);
		this.uploadFont = this.uploadFont.bind(this);
	}

	render() {
		const { classes } = this.props;
		return (
			<CenteredContent>
				<StickyHeader>
					<Header>{localize('fonts-header')}</Header>
					<Description
						variant='body2'
						dangerousInnerHTML={localize('fonts-description')}
					/>
					<Button
						feature='FONT_UPLOAD'
						variant='contained'
						color='primary'
						data-tid="button-fonts-upload"
						onClick={() => this.uploader.openPicker()}
						title={localize('fonts-upload-button')}
						className={classes.uploadButton}
					>
						{localize('fonts-upload-button')}
					</Button>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell padding='none' style={{ border: 0 }}>
									{localize('fonts-table-header')}
								</TableCell>
							</TableRow>
						</TableHead>
					</Table>
				</StickyHeader>
				<Table>
					<TableBody>
						{this.renderRows()}
					</TableBody>
				</Table>
				<FileUploader
					ref={uploader => this.uploader = uploader}
					multiple
					accept={FONTS_ACCEPTED_FILE_TYPES.join(',')}
					onUpload={this.handleUpload}
				/>
			</CenteredContent>
		);
	}

	renderRows() {
		const { fonts } = this.props;

		return fonts.map((font, index) => {
			return (
				<TableRow key={`font-${index}`} hover data-tid={`button-fonts-row-${index}`}>
					<TableCell padding='none'>{font.internalFontId}</TableCell>
					<TableCell align='right' padding='none'>
						<IconButton
							data-tid={`button-fonts-delete-${index}`}
							color='secondary'
							component='span'
							onClick={() => this.handleRemove(font)}
							aria-label='Delete'
						>
							<DeleteIcon />
						</IconButton>
					</TableCell>
				</TableRow>
			);
		});
	}

	handleRemove(font) {
		this.props.showModal('CONFIRMATION_MODAL', {
			message: localize('fonts-delete-confirmation'),
			buttons: [
				<Button
					data-tid="button-fonts-confirm-cancel"
					key='button-cancel'
					variant='outlined'
					color='primary'
				>
					{localize('cancel')}
				</Button>,
				<Button
					key='button-confirm'
					data-tid="`button-fonts-confirm-delete"
					variant='contained'
					color='primary'
					onClick={() => this.props.removeCompanyFont(font)}
				>
					{localize('delete')}
				</Button>
				
			]
		});
	}

	handleUpload(fonts) {
		this.uploadSequence([...fonts], font => {
			return new Promise(resolve => {
				const exists = this.fontExists(font);
				const uploadAndResolve = () => this.uploadFont(font, exists).then(resolve);
				if (exists) {
					this.triggerFontExistsPopup(font, uploadAndResolve, resolve);
				} else {
					uploadAndResolve();
				}
			});
		});
	}

	uploadSequence(fonts, callback) {
		return fonts.reduce((promise, task) => promise.then(() => callback(task)), Promise.resolve());
	}

	uploadFont(font, replaceMode) {
		const data = new FormData();
		data.append('font', font);
		changeCursor(PROGRESS);
		return this.props.addCompanyFont(data, replaceMode).then((response) => {

			if (response instanceof AppError) {
				const error_id = _.get(response, 'originalErrors[0].errorId');
				const status = _.get(response, 'originalErrors[0].status');

				if (status === 413) {
					// A bit different way of handling server error. Middleware have a problem with providing errorId
					// in this case so we rely on server response status code (P9052-10423).
					this.triggerFontSizeExceededPopup();
				} else {
					this.props.showErrorPopup(error_id || ERRORS.UNKNOWN);
				}
			}
			changeCursor();

		});
	}

	fontExists(font) {
		const name = font.name.toLowerCase();
		const existing = this.props.fonts.filter(font => font.internalFontId.toLowerCase() === name);
		return existing.length > 0;
	}

	triggerFontExistsPopup(font, replaceCallback, skipCallback) {
		this.props.showModal('CONFIRMATION_MODAL', {
			message: localize('fonts-font-exists-confirmation', font.name),
			buttons: [
				<Button
					key='button-cancel'
					variant='outlined'
					color='primary'
					onClick={skipCallback}
				>
					{localize('skip')}
				</Button>,
				<Button
					key='button-confirm'
					variant='contained'
					color='primary'
					onClick={replaceCallback}
				>
					{localize('replace')}
				</Button>
			]
		});
	}

	triggerFontSizeExceededPopup() {
		this.props.showModal('CONFIRMATION_MODAL', {
			message: localize('fonts-size-exceeded'),
			buttons: [
				<Button
					key='button-confirm'
					variant='contained'
					color='primary'
				>
					{localize('close')}
				</Button>
			]
		});
	}

}

Fonts.propTypes = {
	classes: PropTypes.object,
	fonts: PropTypes.array,
	showModal: PropTypes.func,
	removeCompanyFont: PropTypes.func,
	addCompanyFont: PropTypes.func,
	showErrorPopup: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
	fonts: _.get(state.companies.rawDataById, `${state.companies.selectedId}.fonts`, [])
});

const mapDispatchToProps = dispatch => bindActionCreators({
	showModal,
	addCompanyFont,
	removeCompanyFont,
	showErrorPopup
}, dispatch);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Fonts);


// WEBPACK FOOTER //
// ./src/containers/Fonts/Fonts.js